/* eslint-disable no-undef */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
    makeStyles,
    Tooltip,
    Button,
    DialogContent,
    Dialog,
    Checkbox,
    FormControlLabel,
} from '@material-ui/core';
import Dropzone from 'react-dropzone';
import { useQuery, useMutation } from '@apollo/client';
import { WATERMARK_SPOTS, FetchPolicy, ALL_LOTS } from 'utils/enum/Core';
import LotsCategory from 'utils/enum/LotsCategory';
import { ConfirmDialogOperation, InventorySection, InventoryCategory } from 'utils/enum/InventoryEnum';
import ModalUtils from 'utils/ModalUtils';
import ImagesUtils from 'utils/ImagesUtils';
import { modules } from 'utils/enum/modules';
import GeneralQuery from 'services/graphQL/query/GeneralQuery';
import GeneralMutation from 'services/graphQL/mutate/GeneralMutation';
import LotQuery from 'services/graphQL/query/LotQuery';
import Select from 'components/widgets/Select';
import If from 'components/widgets/conditional/If';
import PhotosTabStyle from 'styles/modules/inventory/read/PhotosTabStyle';
import ButtonStyles from 'styles/theme/Button';
import CheckOutlinedIcon from '@material-ui/icons/CheckOutlined';
import CloseOutlinedIcon from '@material-ui/icons/CloseOutlined';
import OpenWithOutlinedIcon from '@material-ui/icons/OpenWithOutlined';
import InboxOutlinedIcon from '@material-ui/icons/InboxOutlined';
import CameraAltOutlinedIcon from '@material-ui/icons/CameraAltOutlined';
import DeleteOutlineOutlinedIcon from '@material-ui/icons/DeleteOutlineOutlined';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
import Loading from 'components/widgets/Loading';
import DialogAppBar from 'components/widgets/modal/DialogAppBar';
import ImageList from 'components/modules/inventory/read/panels/gallery/ImageList';
import GetAppIcon from '@material-ui/icons/GetApp';
import CircularProgress from '@material-ui/core/CircularProgress';

const useStyles = makeStyles((theme) => PhotosTabStyle.getStyle(theme));
const buttonStyles = makeStyles((theme) => ButtonStyles.getStyle(theme));

const PhotosTab = ({
    canWrite,
    images,
    addImages,
    toogleReorderMode,
    isUploadingImages,
    isReorderModeOn,
    onSortEnd,
    saveImagesPosition,
    watermark,
    watermarks,
    updateWatermark,
    selectedImages,
    toogleImageSelection,
    onSelectAllImages,
    onConfirmDialogOpen,
    insertMarketingImages,
    isInsertingMarketingImages,
}) => {
    const classes = { ...useStyles(), ...buttonStyles() };
    const hasImages = images.length > 0;
    const isReorderDisabled = images.length < 2;
    const [state, setState] = useState({
        isPreviweOpen: false,
        watermarkedImageURL: null,
        resources: [],
        insertResourceAt: null,
    });

    const {
        data: resourcesData,
        loading: resourcesLoading,
        error: resourcesError,
    } = useQuery(GeneralQuery.PULL_COMMON_RESOURCES, {
        variables: {
            module: modules.INVENTORY,
            section: InventorySection.ADVERTISING,
        },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });

    const { ADVERTISING_IMAGE_INSERT_AT } = InventoryCategory;
    const {
        data: settingsData,
        loading: settingsLoading,
        error: settingsError,
    } = useQuery(LotQuery.GET_SETTINGS, {
        variables: {
            category: LotsCategory.INVENTORY,
            lotName: ALL_LOTS,
            key: [ADVERTISING_IMAGE_INSERT_AT],
        },
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });

    const [downloadImages, { loading: isDownloadingImages }] = useMutation(GeneralMutation.DOWNLOAD_IMAGES);

    useEffect(() => {
        if (resourcesError) {
            ModalUtils.errorMessage(resourcesError?.graphQLErrors);
            return;
        }

        if (!resourcesLoading) {
            const resources = resourcesData?.pullCommonResources;
            if (resources) {
                setState((prevState) => ({
                    ...prevState,
                    resources: resources.slice().sort((a, b) => a.resourceName.localeCompare(b.resourceName)),
                }));
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resourcesLoading, resourcesError]);

    useEffect(() => {
        if (settingsError) {
            ModalUtils.errorMessage(settingsError?.graphQLErrors);
            return;
        }

        if (!settingsLoading) {
            const settings = settingsData?.getSettings;
            if (settings) {
                const insertAt = settings.find((setting) => setting.key === ADVERTISING_IMAGE_INSERT_AT)?.value ?? 3;
                setState((prevState) => ({
                    ...prevState,
                    insertResourceAt: Number(insertAt),
                }));
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settingsLoading, settingsError]);

    const togglePreviewDialog = () => {
        setState({

            ...state,
            watermarkedImageURL: state.isPreviweOpen ? null : state.watermarkedImageURL,
            isPreviweOpen: !state.isPreviweOpen,
        });
    };

    const previewWatermark = async (index) => {
        try {
            const watermarkConfig = {
                url: watermark,
                spot: WATERMARK_SPOTS.OVERLAY,
                padding: 0,
            };

            const current = images[index];
            const file = await ImagesUtils.loadImageUrlIntoFile(current);
            const watermarkedFile = await ImagesUtils.addWatermarks(file, [watermarkConfig]);
            const localURL = URL.createObjectURL(watermarkedFile);

            setState({

                ...state,
                watermarkedImageURL: localURL,
                isPreviweOpen: true,
            });
        } catch (error) {
            ModalUtils.errorMessage(null, error.message);
        }
    };

    const onDownloadImages = async () => {
        const imagesToDownload = selectedImages.map((index) => {
            const imageUrl = images[index];
            const fileName = imageUrl.split('/').pop() || `image_${index + 1}.jpg`;
            return { url: imageUrl, fileName };
        });

        try {
            const { data } = await downloadImages({
                variables: { images: imagesToDownload },
            });

            const results = data?.downloadImages || [];
            results.forEach((result) => {
                if (result.success && result.base64) {
                    const byteCharacters = atob(result.base64);
                    const byteNumbers = new Array(byteCharacters.length);
                    for (let i = 0; i < byteCharacters.length; i += 1) {
                        byteNumbers[i] = byteCharacters.charCodeAt(i);
                    }
                    const byteArray = new Uint8Array(byteNumbers);
                    const blob = new Blob([byteArray], { type: result.mimeType });
                    const url = URL.createObjectURL(blob);

                    const link = document.createElement('a');
                    link.href = url;
                    link.download = result.fileName;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);
                } else if (result.error) {
                    ModalUtils.errorMessage(null, `Error downloading ${result.fileName}: ${result.error}`);
                }
            });
        } catch (error) {
            ModalUtils.errorMessage(error?.graphQLErrors, 'Error downloading images');
        }
    };

    const drawWatermarkComponent = (isSecondHeader = false) => (
        <div className={clsx(classes.watermarkSection, { [classes.secondHeaderWatermark]: isSecondHeader })}>
            <div className={classes.watermarksContainer}>
                <Select
                    nowrap
                    size="sm"
                    name="watermark"
                    loading={false}
                    className={classes.input}
                    onChange={(_, value) => updateWatermark(value)}
                    value={watermark || ''}
                    options={watermarks.map((wm) => ({
                        value: wm.url,
                        label: wm.name,
                    }))}
                />
                <div className={classes.previewWarning}>
                    Select watermark and click
                    <VisibilityOutlinedIcon />
                    to preview
                </div>
            </div>
            <Button
                disabled={isReorderModeOn || selectedImages.length === 0 || isUploadingImages || !canWrite}
                className={clsx(classes.containedInfo, classes.actionButton)}
                onClick={() => onConfirmDialogOpen(ConfirmDialogOperation.WATERMARK)}
            >
                Watermark
            </Button>
        </div>
    );

    return (
        <div className={classes.main}>
            <If condition={isUploadingImages}>
                <Loading className={classes.loader} />
            </If>
            <div className={clsx('d-flex', classes.header)}>
                <Dropzone
                    disabled={isUploadingImages || !canWrite}
                    multiple
                    accept="image/*"
                    onDrop={(acceptedFiles) => addImages(acceptedFiles)}
                    className={clsx(classes.dragzone, !canWrite ? classes.disabledDragzone : '')}
                >
                    <p className={classes.uploadText}>
                        Click or drag file to this area to upload
                    </p>
                    <InboxOutlinedIcon />
                </Dropzone>
                {watermarks.length > 0 && (
                    drawWatermarkComponent()
                )}
                <If condition={!isReorderModeOn}>
                    <Button
                        disabled={isReorderDisabled || !canWrite}
                        className={clsx(classes.containedInfo, classes.actionButton)}
                        startIcon={<OpenWithOutlinedIcon />}
                        onClick={toogleReorderMode}
                    >
                        Reorder
                    </Button>
                </If>
                <If condition={isReorderModeOn}>
                    <Button
                        className={clsx(classes.containedInfo, classes.actionButton)}
                        startIcon={<CheckOutlinedIcon />}
                        onClick={saveImagesPosition}
                    >
                        Apply
                    </Button>
                    <Button
                        className={clsx(classes.containedError, classes.actionButton)}
                        startIcon={<CloseOutlinedIcon />}
                        onClick={toogleReorderMode}
                    >
                        Cancel
                    </Button>
                </If>
                <div className={classes.counter}>
                    <div>
                        <Button
                            disabled={
                                settingsLoading
                                || state.resources.length === 0
                                || images.length === 0
                                || isReorderModeOn
                                || isUploadingImages
                                || isInsertingMarketingImages
                                || !canWrite
                            }
                            className={clsx(classes.containedInfo, classes.actionButton)}
                            onClick={() => insertMarketingImages(state.resources, state.insertResourceAt)}
                        >
                            Insert marketing images
                        </Button>
                        <Button
                            className={clsx(classes.containedInfo, classes.actionButton)}
                            disabled={selectedImages.length === 0 || isDownloadingImages}
                            onClick={onDownloadImages}
                            startIcon={isDownloadingImages ? <CircularProgress size={20} /> : <GetAppIcon />}
                        >
                            Download
                        </Button>
                        <Button
                            disabled={isReorderModeOn || selectedImages.length === 0 || isUploadingImages || !canWrite}
                            className={clsx(classes.containedError, classes.actionButton)}
                            startIcon={<DeleteOutlineOutlinedIcon />}
                            onClick={() => onConfirmDialogOpen(ConfirmDialogOperation.DELETE)}
                        >
                            Delete
                        </Button>
                    </div>
                    {hasImages && (
                        <Tooltip title="Vehicle Photos" placement="top">
                            <span className={classes.photoInfo}>
                                <CameraAltOutlinedIcon className={classes.photoIcon} />
                                {`${images.length}/60`}
                            </span>
                        </Tooltip>
                    )}
                    {!hasImages && (
                        <div className={classes.noData}>
                            No images are available
                        </div>
                    )}
                </div>
            </div>
            <div className={clsx('d-flex', classes.header, classes.secondHeader)}>
                {watermarks.length > 0 && drawWatermarkComponent(true)}
                <FormControlLabel
                    control={(
                        <Checkbox
                            checked={hasImages && selectedImages.length === images.length}
                            indeterminate={selectedImages.length > 0 && selectedImages.length < images.length}
                            onChange={(e) => onSelectAllImages(e.target.checked)}
                            disabled={!hasImages || isUploadingImages || !canWrite}
                            className={classes.selectAllCheckbox}
                            color="primary"
                        />
                    )}
                    label="Select All"
                />
            </div>
            {hasImages && (
                <ImageList
                    canWrite={canWrite}
                    images={images}
                    onSortEnd={onSortEnd}
                    isReorderModeOn={isReorderModeOn}
                    selectedImages={selectedImages}
                    toogleImageSelection={toogleImageSelection}
                    previewWatermark={previewWatermark}
                    watermarksAvailable={watermarks && watermarks.length > 0}
                />
            )}
            <Dialog
                open={state.isPreviweOpen}
                maxWidth="md"
                disableBackdropClick
                disableEscapeKeyDown
                scroll="paper"
                onMouseDown={(e) => e.stopPropagation()}
            >
                <DialogAppBar
                    appBarClassName={classes.AppBar}
                    title="Preview"
                    onClose={togglePreviewDialog}
                    toolbarSize="md"
                />
                <DialogContent className={classes.previewedImage}>
                    {state.watermarkedImageURL && (
                        <img
                            alt="Watermarked"
                            src={state.watermarkedImageURL}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

PhotosTab.propTypes = {
    images: PropTypes.array,
    addImages: PropTypes.func.isRequired,
    isInsertingMarketingImages: PropTypes.bool.isRequired,
    isUploadingImages: PropTypes.bool.isRequired,
    isReorderModeOn: PropTypes.bool.isRequired,
    toogleReorderMode: PropTypes.func.isRequired,
    onSortEnd: PropTypes.func.isRequired,
    saveImagesPosition: PropTypes.func.isRequired,
    watermark: PropTypes.string,
    watermarks: PropTypes.array.isRequired,
    updateWatermark: PropTypes.func.isRequired,
    selectedImages: PropTypes.array,
    toogleImageSelection: PropTypes.func,
    onSelectAllImages: PropTypes.func,
    onConfirmDialogOpen: PropTypes.func.isRequired,
    insertMarketingImages: PropTypes.func.isRequired,
    canWrite: PropTypes.bool.isRequired,
};

PhotosTab.defaultProps = {
    images: [],
    watermark: '',
    selectedImages: [],
    toogleImageSelection: () => null,
    onSelectAllImages: () => null,
};

export default PhotosTab;
